import { useState } from "react"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { useThreadRuntime } from "@assistant-ui/react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Clock01Icon,
  MessageAdd01Icon,
  MoreHorizontalIcon,
  Refresh01Icon,
} from "@hugeicons/core-free-icons"
import { useChatPanel } from "../hooks/use-chat-panel"
import { useAbandonDraft } from "../queries/assistant-queries"

/** Header dropdown menu with thread-level actions. */
export function ThreadActionsMenu() {
  const view = useChatPanel((s) => s.view)
  const issueId = useChatPanel((s) => s.threadId) // DRAFT issue id
  const issueStatus = useChatPanel((s) => s.threadStatus)
  const hasAssistantReplied = useChatPanel((s) => s.hasAssistantReplied)
  const setView = useChatPanel((s) => s.setView)
  const clearThread = useChatPanel((s) => s.clearThread)
  const threadRuntime = useThreadRuntime()
  const qc = useQueryClient()
  const abandonMut = useAbandonDraft()

  const [confirmOpen, setConfirmOpen] = useState(false)

  const viewingActive = issueStatus === "DRAFT" || issueStatus === null
  const showNewChat = issueId != null || hasAssistantReplied
  const canViewHistory = view === "chat"

  // Menu is always available in chat view (at minimum "View history"
  // is present); in history view the panel has its own Back arrow so
  // no menu items apply.
  if (view === "history") return null

  const reset = () => {
    clearThread()
    threadRuntime.reset([])
    qc.invalidateQueries({ queryKey: ["chat-threads"] })
  }

  const handleNewChat = async () => {
    try {
      // Abandon the current DRAFT if we're in one; otherwise just reset
      // the client state (viewing a past submitted/abandoned issue).
      if (viewingActive && issueId != null) {
        await abandonMut.mutateAsync(issueId)
      }
      reset()
      toast.success("Started a new conversation")
    } catch (err) {
      toast.error("Couldn't start a new chat. Please try again.")
      console.error(err)
    } finally {
      setConfirmOpen(false)
    }
  }

  const handleHistory = () => setView("history")

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              title="More actions"
            />
          }
        >
          <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">
          {canViewHistory && (
            <DropdownMenuItem onClick={handleHistory}>
              <HugeiconsIcon icon={Clock01Icon} />
              View history
            </DropdownMenuItem>
          )}
          {canViewHistory && showNewChat && <DropdownMenuSeparator />}
          {showNewChat && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
            >
              <HugeiconsIcon
                icon={viewingActive ? Refresh01Icon : MessageAdd01Icon}
              />
              {viewingActive ? "Start over" : "New chat"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current conversation will be discarded and you'll see the
              welcome screen again. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={handleNewChat}
              disabled={abandonMut.isPending}
            >
              {abandonMut.isPending ? "Discarding…" : "Start new chat"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
